(function () {
  'use strict';

  const SELECTORS = {
    drawer: 'cart-drawer',
    recommendations: '[data-recommendations-section]',
    addButton: '[data-recommendation-add]'
  };

  const state = {
    loadingRecommendations: new Set(),
    addingProducts: new Set(),
    lastAddedProductId: null
  };

  /**
   * -------------------------------------------------------
   * Get current cart
   * -------------------------------------------------------
   */
  async function getCart() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${response.status}`
      );
    }

    return response.json();
  }

  /**
   * -------------------------------------------------------
   * Find product ID for recommendation
   *
   * Priority:
   *
   * 1. Product that was just added
   * 2. Existing loaded recommendation product
   * 3. First cart product as fallback
   * -------------------------------------------------------
   */
  async function getRecommendationProductId() {
    if (state.lastAddedProductId) {
      return state.lastAddedProductId;
    }

    const cart = await getCart();

    if (
      !cart ||
      !Array.isArray(cart.items) ||
      !cart.items.length
    ) {
      return null;
    }

    return cart.items[0]?.product_id || null;
  }

  /**
   * -------------------------------------------------------
   * Build recommendation URL
   * -------------------------------------------------------
   */
  function buildRecommendationUrl(
    element,
    productId
  ) {
    const sectionId =
      element.dataset.recommendationsSection;

    const limit =
      element.dataset.recommendationsLimit || 4;

    const intent =
      element.dataset.recommendationsIntent ||
      'related';

    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: productId,
      limit: limit,
      intent: intent
    });

    return (
      `${window.Shopify.routes.root}` +
      `recommendations/products?` +
      params.toString()
    );
  }

  /**
   * -------------------------------------------------------
   * Load recommendations
   * -------------------------------------------------------
   */
  async function loadRecommendations(
    element,
    productId,
    force = false
  ) {
    if (!element || !productId) {
      return;
    }

    /*
     * Don't request the same product again
     * unless explicitly forced.
     */
    if (
      !force &&
      element.dataset.loadedProductId ===
        String(productId)
    ) {
      return;
    }

    const requestKey =
      `${productId}:${element.dataset.recommendationsSection}`;

    if (
      state.loadingRecommendations.has(
        requestKey
      )
    ) {
      return;
    }

    state.loadingRecommendations.add(
      requestKey
    );

    element.classList.remove('is-empty');
    element.classList.add('is-loading');

    try {
      const url =
        buildRecommendationUrl(
          element,
          productId
        );

      const response = await fetch(url, {
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Recommendation request failed: ${response.status}`
        );
      }

      const html =
        await response.text();

      const parser =
        new DOMParser();

      const documentHTML =
        parser.parseFromString(
          html,
          'text/html'
        );

      const newRecommendations =
        documentHTML.querySelector(
          SELECTORS.recommendations
        );

      if (!newRecommendations) {
        element.innerHTML = '';
        element.classList.add('is-empty');
        return;
      }

      const content =
        newRecommendations.innerHTML.trim();

      if (!content) {
        element.innerHTML = '';
        element.classList.add('is-empty');
        return;
      }

      /*
       * Replace recommendation content.
       */
      element.innerHTML = content;

      /*
       * Remember which product generated
       * the current recommendations.
       */
      element.dataset.loadedProductId =
        String(productId);

      element.classList.remove(
        'is-loading'
      );

      element.classList.add(
        'is-loaded'
      );

      initializeAddButtons(element);

    } catch (error) {
      console.warn(
        '[Cart Recommendations]',
        error
      );

      element.innerHTML = '';

      element.classList.remove(
        'is-loading'
      );

      element.classList.add(
        'is-empty'
      );

    } finally {
      state.loadingRecommendations.delete(
        requestKey
      );
    }
  }

  /**
   * -------------------------------------------------------
   * Initialize recommendation container
   * -------------------------------------------------------
   */
  async function initializeRecommendations(
    element
  ) {
    if (!element) {
      return;
    }

    const productId =
      await getRecommendationProductId();

    if (!productId) {
      element.innerHTML = '';

      element.classList.add(
        'is-empty'
      );

      return;
    }

    await loadRecommendations(
      element,
      productId
    );
  }

  /**
   * -------------------------------------------------------
   * Initialize all recommendation containers
   * -------------------------------------------------------
   */
  function initializeAllRecommendations() {
    const containers =
      document.querySelectorAll(
        SELECTORS.recommendations
      );

    containers.forEach(
      (container) => {
        initializeRecommendations(
          container
        );
      }
    );
  }

  /**
   * -------------------------------------------------------
   * Add recommendation product
   * -------------------------------------------------------
   */
  async function addRecommendation(
    button
  ) {
    if (!button) {
      return;
    }

    const variantId =
      button.dataset.variantId;

    const productId =
      button
        .closest(
          '.cart-drawer-recommendation'
        )
        ?.dataset.productId;

    if (!variantId) {
      return;
    }

    if (
      state.addingProducts.has(
        variantId
      )
    ) {
      return;
    }

    state.addingProducts.add(
      variantId
    );

    button.disabled = true;
    button.classList.add(
      'is-loading'
    );

    const label =
      button.querySelector(
        '.cart-drawer-recommendation__add-label'
      );

    const spinner =
      button.querySelector(
        '.cart-drawer-recommendation__spinner'
      );

    if (label) {
      label.classList.add('hidden');
    }

    if (spinner) {
      spinner.classList.remove(
        'hidden'
      );
    }

    try {
      const response =
        await fetch(
          `${window.Shopify.routes.root}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: 1
                }
              ],

              sections: [
                'cart-drawer',
                'cart-icon-bubble'
              ],

              sections_url:
                window.location.pathname
            })
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'Unable to add product'
        );
      }

      /*
       * IMPORTANT:
       *
       * Save the product that was JUST added.
       *
       * This becomes the source product for
       * the next recommendation request.
       */
      if (productId) {
        state.lastAddedProductId =
          String(productId);
      }

      /*
       * Update Dawn Cart Drawer.
       */
      const cartDrawer =
        document.querySelector(
          SELECTORS.drawer
        );

      if (
        cartDrawer &&
        typeof cartDrawer.renderContents ===
          'function' &&
        data.sections
      ) {
        cartDrawer.renderContents(
          data
        );
      }

      /*
       * Wait until Dawn finishes replacing
       * the drawer markup.
       *
       * Then force recommendation reload.
       */
      setTimeout(() => {
        refreshRecommendations(
          state.lastAddedProductId
        );
      }, 200);

    } catch (error) {
      console.error(
        '[Cart Recommendations]',
        error
      );

      button.disabled = false;

      button.classList.remove(
        'is-loading'
      );

      if (label) {
        label.classList.remove(
          'hidden'
        );
      }

      if (spinner) {
        spinner.classList.add(
          'hidden'
        );
      }

    } finally {
      state.addingProducts.delete(
        variantId
      );
    }
  }

  /**
   * -------------------------------------------------------
   * Refresh recommendations using
   * the newest added product.
   * -------------------------------------------------------
   */
  function refreshRecommendations(
    productId
  ) {
    if (!productId) {
      initializeAllRecommendations();
      return;
    }

    const containers =
      document.querySelectorAll(
        SELECTORS.recommendations
      );

    containers.forEach(
      (container) => {

        /*
         * Clear previous state so that
         * the new product is guaranteed
         * to trigger a request.
         */
        delete container.dataset
          .loadedProductId;

        loadRecommendations(
          container,
          productId,
          true
        );
      }
    );
  }

  /**
   * -------------------------------------------------------
   * Initialize Add buttons
   * -------------------------------------------------------
   */
  function initializeAddButtons(
    container
  ) {
    if (!container) {
      return;
    }

    const buttons =
      container.querySelectorAll(
        SELECTORS.addButton
      );

    buttons.forEach(
      (button) => {

        if (
          button.dataset
            .recommendationInitialized ===
          'true'
        ) {
          return;
        }

        button.dataset
          .recommendationInitialized =
          'true';

        button.addEventListener(
          'click',
          (event) => {
            event.preventDefault();

            addRecommendation(
              button
            );
          }
        );
      }
    );
  }

  /**
   * -------------------------------------------------------
   * Observe Cart Drawer DOM changes
   * -------------------------------------------------------
   */
  function observeCartDrawer() {
    const drawer =
      document.querySelector(
        SELECTORS.drawer
      );

    if (!drawer) {
      return;
    }

    const observer =
      new MutationObserver(
        (mutations) => {

          let recommendationAdded =
            false;

          mutations.forEach(
            (mutation) => {

              if (
                mutation.type !==
                'childList'
              ) {
                return;
              }

              mutation.addedNodes.forEach(
                (node) => {

                  if (
                    node.nodeType !==
                    Node.ELEMENT_NODE
                  ) {
                    return;
                  }

                  if (
                    node.matches?.(
                      SELECTORS.recommendations
                    )
                  ) {
                    recommendationAdded =
                      true;
                  }

                  if (
                    node.querySelector?.(
                      SELECTORS.recommendations
                    )
                  ) {
                    recommendationAdded =
                      true;
                  }
                }
              );
            }
          );

          if (
            recommendationAdded
          ) {
            /*
             * If we already know the newest
             * added product, use it.
             */
            if (
              state.lastAddedProductId
            ) {
              refreshRecommendations(
                state.lastAddedProductId
              );
            } else {
              initializeAllRecommendations();
            }
          }
        }
      );

    observer.observe(
      drawer,
      {
        childList: true,
        subtree: true
      }
    );
  }

  /**
   * -------------------------------------------------------
   * Listen for cart icon click
   * -------------------------------------------------------
   */
  function observeCartOpen() {
    document.addEventListener(
      'click',
      (event) => {

        const cartTrigger =
          event.target.closest(
            '#cart-icon-bubble'
          );

        if (!cartTrigger) {
          return;
        }

        setTimeout(() => {

          /*
           * If a product was previously added,
           * keep using it.
           */
          if (
            state.lastAddedProductId
          ) {
            refreshRecommendations(
              state.lastAddedProductId
            );
          } else {
            initializeAllRecommendations();
          }

        }, 250);
      }
    );
  }

  /**
   * -------------------------------------------------------
   * Listen to standard Shopify cart events
   * -------------------------------------------------------
   */
  document.addEventListener(
    'cart-drawer-recommendation-added',
    () => {

      setTimeout(() => {

        refreshRecommendations(
          state.lastAddedProductId
        );

      }, 100);

    }
  );

  /**
   * -------------------------------------------------------
   * Init
   * -------------------------------------------------------
   */
  function init() {
    initializeAllRecommendations();
    observeCartDrawer();
    observeCartOpen();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

})();